import BigNumber from 'bignumber.js';

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

// Regular expressions that matches characters otherwise inexpressible in JSON (U+0022 QUOTATION MARK, U+005C REVERSE SOLIDUS, and ASCII control characters U+0000 through U+001F) or UTF-8 (U+D800 through U+DFFF)
var forbidden = /(?:[\0-\x1F"\\]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])/;

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};





var asyncGenerator = function () {
  function AwaitValue(value) {
    this.value = value;
  }

  function AsyncGenerator(gen) {
    var front, back;

    function send(key, arg) {
      return new Promise(function (resolve, reject) {
        var request = {
          key: key,
          arg: arg,
          resolve: resolve,
          reject: reject,
          next: null
        };

        if (back) {
          back = back.next = request;
        } else {
          front = back = request;
          resume(key, arg);
        }
      });
    }

    function resume(key, arg) {
      try {
        var result = gen[key](arg);
        var value = result.value;

        if (value instanceof AwaitValue) {
          Promise.resolve(value.value).then(function (arg) {
            resume("next", arg);
          }, function (arg) {
            resume("throw", arg);
          });
        } else {
          settle(result.done ? "return" : "normal", result.value);
        }
      } catch (err) {
        settle("throw", err);
      }
    }

    function settle(type, value) {
      switch (type) {
        case "return":
          front.resolve({
            value: value,
            done: true
          });
          break;

        case "throw":
          front.reject(value);
          break;

        default:
          front.resolve({
            value: value,
            done: false
          });
          break;
      }

      front = front.next;

      if (front) {
        resume(front.key, front.arg);
      } else {
        back = null;
      }
    }

    this._invoke = send;

    if (typeof gen.return !== "function") {
      this.return = undefined;
    }
  }

  if (typeof Symbol === "function" && Symbol.asyncIterator) {
    AsyncGenerator.prototype[Symbol.asyncIterator] = function () {
      return this;
    };
  }

  AsyncGenerator.prototype.next = function (arg) {
    return this._invoke("next", arg);
  };

  AsyncGenerator.prototype.throw = function (arg) {
    return this._invoke("throw", arg);
  };

  AsyncGenerator.prototype.return = function (arg) {
    return this._invoke("return", arg);
  };

  return {
    wrap: function (fn) {
      return function () {
        return new AsyncGenerator(fn.apply(this, arguments));
      };
    },
    await: function (value) {
      return new AwaitValue(value);
    }
  };
}();





var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();









































var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

var escapee = {
  '"': '"',
  '\\': '\\',
  '/': '/',
  b: '\b',
  f: '\f',
  n: '\n',
  r: '\r',
  t: '\t' };


// This is a class that can parse a JSON text, producing a JavaScript
// data structure. It is a simple, recursive descent parser. It does not use
// eval or regular expressions, so it can be used as a model for implementing
// a JSON parser in other languages.
var Parser = function () {
  function Parser(source, reviver) {classCallCheck(this, Parser);
    this.text = '' + source;
    this.at = 0;
    this.ch = ' ';
    this.reviver = reviver;
  }createClass(Parser, [{ key: 'error', value: function error(

    m) {
      return new Error(
      JSON.stringify({
        name: 'SyntaxError',
        message: m,
        at: this.at,
        text: this.text,
        context: this.chars.slice(this.at - 5, this.at + 5).join() }));


    } }, { key: 'next', value: function next(

    c) {
      // If a c parameter is provided, verify that it matches the current character.
      if (c && c !== this.ch) {
        throw this.error('Expected \'' + c + '\' instead of \'' + this.ch + '\'');
      }

      // Get the next character. When there are no more characters,
      // return the empty string.
      this.ch = this.chars[this.at];
      this.at += 1;
      return this.ch;
    }

    // Parses a number value.
  }, { key: 'number', value: function number() {
      var string = '';

      if (this.ch === '-') {
        string = '-';
        this.next('-');
      }

      while (this.ch >= '0' && this.ch <= '9') {
        string += this.ch;
        this.next();
      }
      if (this.ch === '.') {
        string += '.';
        this.next();
        if (this.ch >= '0' && this.ch <= '9') {
          string += this.ch;
        } else {
          throw this.error('Bad number: partial fraction number');
        }

        while (this.next() && this.ch >= '0' && this.ch <= '9') {
          string += this.ch;
        }
      }
      if (this.ch === 'e' || this.ch === 'E') {
        string += this.ch;
        this.next();
        if (this.ch === '-' || this.ch === '+') {
          string += this.ch;
          this.next();
        }
        while (this.ch >= '0' && this.ch <= '9') {
          string += this.ch;
          this.next();
        }
      }
      if (string.match(/^-?0\d/)) {
        throw this.error('Bad number: leading zero');
      }

      if (string.length > 15 || string.toUpperCase().indexOf('E') >= 0) {
        return new BigNumber(string);
      }
      return +string;
    }

    // Parses a string value.
  }, { key: 'string', value: function string() {
      var string = '';

      // When parsing for string values, we must look for " and \ characters.
      if (this.ch === '"') {
        while (this.next()) {
          if (this.ch === '"') {
            this.next();
            return string;
          }
          if (this.ch === '\\') {
            this.next();
            if (this.ch === 'u') {
              var uffff = 0;
              for (var i = 0; i < 4; i += 1) {
                var hex = parseInt(this.next(), 16);
                if (!Number.isFinite(hex)) {
                  throw this.error('Bad unicode code point');
                }
                uffff = uffff * 16 + hex;
              }
              string += String.fromCodePoint(uffff);
            } else if (typeof escapee[this.ch] === 'string') {
              string += escapee[this.ch];
            } else {
              break;
            }
          } else if (this.ch.match(forbidden)) {
            break;
          } else {
            string += this.ch;
          }
        }
      }
      throw this.error('Bad string');
    }

    // Skips whitespace.
  }, { key: 'white', value: function white() {
      while (this.ch && this.ch <= ' ') {
        this.next();
      }
    }

    // true, false, or null.
  }, { key: 'word', value: function word() {
      switch (this.ch) {
        case 't':
          this.next('t');
          this.next('r');
          this.next('u');
          this.next('e');
          return true;
        case 'f':
          this.next('f');
          this.next('a');
          this.next('l');
          this.next('s');
          this.next('e');
          return false;
        case 'n':
          this.next('n');
          this.next('u');
          this.next('l');
          this.next('l');
          return null;
        default:
          throw this.error('Unexpected \'' + this.ch + '\'');}

    }

    // Parses an array value.
  }, { key: 'array', value: function array() {
      var array = [];

      if (this.ch === '[') {
        this.next('[');
        this.white();
        if (this.ch === ']') {
          this.next(']');
          return array; // empty array
        }
        while (this.ch) {
          array.push(this.value());
          this.white();
          if (this.ch === ']') {
            this.next(']');
            return array;
          }
          this.next(',');
          this.white();
        }
      }
      throw this.error('Bad array');
    }

    // Parses an object value.
  }, { key: 'object', value: function object() {
      var key = void 0;
      var object = {};

      if (this.ch === '{') {
        this.next('{');
        this.white();
        if (this.ch === '}') {
          this.next('}');
          return object; // empty object
        }
        while (this.ch) {
          key = this.string();
          this.white();
          this.next(':');
          object[key] = this.value();
          this.white();
          if (this.ch === '}') {
            this.next('}');
            return object;
          }
          this.next(',');
          this.white();
        }
      }
      throw this.error('Bad object');
    }

    // Parses a JSON value. It could be an object, an array, a string, a number,
    // or a word.
  }, { key: 'value', value: function value() {
      this.white();
      switch (this.ch) {
        case '{':
          return this.object();
        case '[':
          return this.array();
        case '"':
          return this.string();
        case '-':
          return this.number();
        default:
          return this.ch >= '0' && this.ch <= '9' ? this.number() : this.word();}

    } }, { key: 'parse', value: function parse()

    {
      this.chars = [].concat(toConsumableArray(this.text));
      var result = this.value();
      this.white();
      if (this.ch) {
        throw this.error('Syntax error');
      }

      // If there is a reviver function, we recursively walk the new structure,
      // passing each name/value pair to the reviver function for possible
      // transformation, starting with a temporary root object that holds the result
      // in an empty key. If there is not a reviver function, we simply return the
      // result.
      return typeof this.reviver === 'function' ?
      this.walk({ '': result }, '') :
      result;
    } }, { key: 'walk', value: function walk(

    holder, key) {var _this = this;
      var v = void 0;
      var value = holder[key];
      if (value && (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object') {
        Object.keys(value).forEach(function (k) {
          v = _this.walk(value, k);
          if (v !== undefined) {
            value[k] = v;
          } else {
            delete value[k];
          }
        });
      }
      return this.reviver.call(null, holder, key, value);
    } }]);return Parser;}();


var parse = function (source, reviver) {
  return new Parser(source, reviver).parse();
};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

// Returns the code point of char left-padded with 0s
var toCodePoint = function (char, length) {
  return ('' + '0'.repeat(length) + char.
  codePointAt(0).
  toString(16).
  toUpperCase()).slice(-length);
};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

var meta = {
  // table of character substitutions
  '\b': '\\b',
  '\t': '\\t',
  '\n': '\\n',
  '\f': '\\f',
  '\r': '\\r',
  '"': '\\"',
  '\\': '\\\\' };


// MUST represent all strings (including object member names) in their minimal-length UTF-8 encoding
// avoiding escape sequences for characters except those otherwise inexpressible in JSON (U+0022 QUOTATION MARK, U+005C REVERSE SOLIDUS, and ASCII control characters U+0000 through U+001F) or UTF-8 (U+D800 through U+DFFF), and
// avoiding escape sequences for combining characters, variation selectors, and other code points that affect preceding characters, and
// using two-character escape sequences where possible for characters that require escaping:
// \b U+0008 BACKSPACE
// \t U+0009 CHARACTER TABULATION ("tab")
// \n U+000A LINE FEED ("newline")
// \f U+000C FORM FEED
// \r U+000D CARRIAGE RETURN
// \" U+0022 QUOTATION MARK
// \\ U+005C REVERSE SOLIDUS ("backslash"), and
// using six-character \u00xx uppercase hexadecimal escape sequences for control characters that require escaping but lack a two-character sequence, and
// using six-character \uDxxx uppercase hexadecimal escape sequences for lone surrogates
var transformString = function (string) {
  if (!forbidden.test(string)) {
    return '"' + string + '"';
  }

  var result = '';

  /* eslint-disable no-restricted-syntax */var _iteratorNormalCompletion = true;var _didIteratorError = false;var _iteratorError = undefined;try {
    for (var _iterator = string[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {var char = _step.value;
      if (forbidden.test(char)) {
        var c = meta[char];
        if (c) {
          result += c;
        } else {
          result += '\\u' + toCodePoint(char, 4);
        }
      } else {
        result += char;
      }
    }} catch (err) {_didIteratorError = true;_iteratorError = err;} finally {try {if (!_iteratorNormalCompletion && _iterator.return) {_iterator.return();}} finally {if (_didIteratorError) {throw _iteratorError;}}}
  return '"' + result + '"';
};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

var emptyFractionalPart = /^(-?\d+)(E-\d+)$/;

function addNonEmptySignificandFractionalPart(dec) {
  var match = dec.match(emptyFractionalPart);
  if (match) {
    return match[1] + '.0' + match[2];
  }
  return dec;
}

// MUST represent all integer numbers (those with a zero-valued fractional part)
// without a leading minus sign when the value is zero, and
// without a decimal point, and
// without an exponent
//
// MUST represent all non-integer numbers in exponential notation
// including a nonzero single-digit significand integer part, and
// including a nonempty significand fractional part, and
// including no trailing zeroes in the significand fractional part (other than as part of a ".0" required to satisfy the preceding point), and
// including a capital "E", and
// including no plus sign in the exponent, and
// including no insignificant leading zeroes in the exponent
var transformNumber = function (value) {
  var val = new BigNumber(value);
  if (val.isInteger()) {
    return val.toFixed();
  }

  return addNonEmptySignificandFractionalPart(
  val.
  toExponential().
  toUpperCase().
  replace('+', ''));

};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

// MUST order the members of all objects lexicographically by the UCS (Unicode Character Set) code points of their names
// - preserving and utilizing the code points in U+D800 through U+DFFF (inclusive) for all lone surrogates
var sortKeys = function (keyA, keyB) {
  if (keyA === keyB) return 0;

  var keyAchars = [].concat(toConsumableArray(keyA));
  var keyBchars = [].concat(toConsumableArray(keyB));

  if (keyAchars.length === 0 && keyBchars.length === 0) {
    return 0;
  }

  var result = -1;

  keyAchars.some(function (aChar, index) {
    var bChar = keyBchars[index];
    if (!bChar) {
      result = 1;
      return true;
    }
    if (aChar.codePointAt(0) !== bChar.codePointAt(0)) {
      result = toCodePoint(aChar, 8).localeCompare(toCodePoint(bChar, 8));
      return true;
    }
    return false;
  });

  return result;
};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

// Joins all of the elements together, separated with commas, and wrap them in
// wrappers ([] or {}).
var join = function (partial, wrappers, gap, mind) {
  if (partial.length === 0) {
    return wrappers;
  }
  return gap ?
  wrappers[0] + '\n' + gap + partial.join(',\n' +
  gap) + '\n' +
  mind + wrappers[1] : '' +
  wrappers[0] + partial.join(',') + wrappers[1];
};

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

function isBigNumber(value) {
  return value && value.constructor && value.constructor.name === 'BigNumber';
}var Transformer = function () {
  function Transformer(gap, indent, replacer) {classCallCheck(this, Transformer);
    this.gap = gap;
    this.indent = indent;
    this.replacer = replacer;
  }

  /**
    * Produces a string from holder[key].
    * @param {*} key
    * @param {*} holder
    */createClass(Transformer, [{ key: 'transform', value: function transform(
    key, holder) {var _this = this;
      var mind = this.gap;

      var value = holder[key];

      // If the value has a toJSON method, call it to obtain a replacement value.
      if (
      value &&
      (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object' &&
      typeof value.toJSON === 'function' &&
      !isBigNumber(value))
      {
        value = value.toJSON(key);
      }

      // If we were called with a replacer function, then call the replacer to
      // obtain a replacement value.
      if (typeof this.replacer === 'function') {
        value = this.replacer.call(null, holder, key, value);
      }

      // What happens next depends on the value's type.
      switch (typeof value === 'undefined' ? 'undefined' : _typeof(value)) {
        case 'string':
          return transformString(value);

        case 'number':
          return transformNumber(value);

        // undefined value must be treated the same way as a null value
        // since the serialization must be consistent across languages
        case 'undefined':
          return 'null';

        case 'boolean':
        case 'null':
          // If the value is a boolean or null, convert it to a string. Note:
          // typeof null does not produce 'null'. The case is included here in
          // the remote chance that this gets fixed someday.
          return String(value);

        // If the type is 'object', we might be dealing with an object or an array or
        // null.

        case 'object':{
            // Due to a specification blunder in ECMAScript, typeof null is 'object',
            // so watch out for that case.
            if (!value) {
              return 'null';
            }

            if (isBigNumber(value)) {
              return transformNumber(value);
            }

            // Make an array to hold the partial results of stringifying this object value.
            var partial = [];

            this.gap += this.indent;

            // Is the value an array?
            if (Array.isArray(value)) {
              // The value is an array. Stringify every element. Use null as a placeholder
              // for non-JSON values.
              value.forEach(function (_, i) {
                partial.push(_this.transform(i, value) || 'null');
              });

              // Join all of the elements together, separated with commas, and wrap them in
              // brackets.
              var _v = join(partial, '[]', this.gap, mind);

              this.gap = mind;
              return _v;
            }

            // If the replacer is an array, use it to select the members to be stringified.
            if (this.replacer && _typeof(this.replacer) === 'object') {
              this.replacer.forEach(function (rep) {
                if (typeof rep === 'string') {
                  var _v2 = _this.transform(rep, value);
                  if (_v2) {
                    partial.push(_this.format(rep, _v2));
                  }
                }
              });
            } else {
              // Otherwise, iterate through all of the keys in the object.
              var keysSorted = Object.keys(value).sort(sortKeys);
              keysSorted.forEach(function (k) {
                if (Object.prototype.hasOwnProperty.call(value, k)) {
                  var _v3 = _this.transform(k, value);
                  if (_v3) {
                    partial.push(_this.format(k, _v3));
                  }
                }
              });
            }

            // Join all of the member texts together, separated with commas,
            // and wrap them in braces.
            var v = join(partial, '{}', this.gap, mind);
            this.gap = mind;
            return v;
          }
        default:
          throw new Error('Unexpected object type: ' + (typeof value === 'undefined' ? 'undefined' : _typeof(value)));}

    } }, { key: 'format', value: function format(

    key, value) {
      return transformString(key) + (this.gap ? ': ' : ':') + value;
    } }]);return Transformer;}();


// The stringify method takes a value and an optional replacer, and an optional
// space parameter, and returns a JSON text. The replacer can be a function
// that can replace values, or an array of strings that will select the keys.
// A default replacer method can be provided. Use of the space parameter can
// produce text that is more easily readable.
function stringify(value, replacer, space) {
  var gap = '';
  var indent = '';

  // If the space parameter is a number, make an indent string containing that
  // many spaces.
  if (typeof space === 'number') {
    indent = ' '.repeat(space);

    // If the space parameter is a string, it will be used as the indent string.
  } else if (typeof space === 'string') {
    indent = space;
  }

  // If there is a replacer, it must be a function or an array.
  // Otherwise, throw an error.
  if (
  replacer &&
  typeof replacer !== 'function' && (
  (typeof replacer === 'undefined' ? 'undefined' : _typeof(replacer)) !== 'object' || typeof replacer.length !== 'number'))
  {
    throw new Error('JSON.stringify');
  }

  // Make a fake root object containing our value under the key of ''.
  // Return the result of stringifying the value.
  return new Transformer(gap, indent, replacer).transform('', { '': value });
}

/*
  Copyright 2017 Stratumn SAS. All rights reserved.

  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.
*/

/*
   The original version of this code is taken from Mirko Kiefer's canonical-json:
   https://github.com/mirkokiefer/canonical-json/blob/master/index.js
   Modifications were made to conform to the canonicaljson spec:
   https://github.com/gibson042/canonicaljson-spec
   */

export { stringify, parse };
//# sourceMappingURL=canonicaljson.mjs.map
